import { Trans, useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { FC, ReactNode } from "react";

import { RouteName } from "@/consts";
import { Eth721Asset } from "@/features/nft/types";
import TextPlaceholder from "@/features/placeholder/TextPlaceholder";
import { NFTDisplayName } from "@/features/post/components/NFTDisplayName";
import {
  CardDate,
  Title,
} from "@/features/post/components/postCard/components";
import { ImageCardProps } from "@/features/post/components/postCard/type";
import { convertWeiToEther } from "@/helpers/etherConversion";

type CollectibleImageCardInformationSectionProps = {
  actionButton: ReactNode;
  asset: Partial<Eth721Asset>;
  assetLoading: boolean;
  post: ImageCardProps["post"];
};

export const CollectibleImageCardInformationSection: FC<CollectibleImageCardInformationSectionProps> =
  (props) => {
    const { t } = useTranslation(["common", "post"]);

    const { post, actionButton, assetLoading, asset } = props;

    const router = useRouter();
    const { route } = router;
    const isPreview = route === `${RouteName.NewPost}/[slug]`;

    return (
      <div className="flex flex-col min-w-0 p-4 mt-0 pt-10 space-y-3">
        {/* Creator & Ending Bit Time */}
        <div className="w-full flex justify-between">
          <div className="flex items-center flex-1 min-w-0 whitespace-pre">
            {asset ? (
              <Trans
                i18nKey="post:imageCard.by"
                components={{
                  name: (
                    <NFTDisplayName
                      className="text-sm font-light text-gray-900 touchable w-full"
                      walletAccount={asset.owner}
                    />
                  ),
                }}
              />
            ) : (
              <TextPlaceholder size="sm" />
            )}
          </div>
          <div className="ml-5">
            <CardDate date={post.createdAt} />
          </div>
        </div>

        <Title
          title={post.title}
          slug={post.slug}
          isPreview={isPreview}
          postId={post._id}
        />

        {/* provide a fixed height to prevent layout shift */}
        <div className="flex items-center justify-between w-full h-10">
          {/* if loading or asset not cached --> loading skeleton */}
          {assetLoading ? (
            <>
              <div className="rounded w-2/4 h-4 bg-gray-200" />
              <div className="rounded-full bg-gray-200 h-9 w-20" />
            </>
          ) : (
            <>
              <span className="text-sm font-light text-gray-400">
                {/* if listed --> display price, else forSaleSoon */}
                {asset?.listStatus ? (
                  <>
                    <Trans
                      i18nKey="common:nft.sellingPrice"
                      values={{
                        amount: `${convertWeiToEther(asset.price)} ETH`, //TODO: smart contract may also keep the state of which currency is used.
                      }}
                      components={{
                        price: <span className="text-primary" />,
                      }}
                    />
                  </>
                ) : (
                  <>
                    <div className="text-gray-800  text-base">
                      {t("common:nft.forSaleSoon")}
                    </div>
                  </>
                )}
              </span>
              {actionButton}
            </>
          )}
        </div>
      </div>
    );
  };
